import {
  Col,
  Divider,
  Form,
  Row,
  Table,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { downloadAs } from '@utils/utils';
import ContentContainer from '../../../components/contentContainer/ContentContainer';
import CustomPageContainer from '../../../components/CustomPageContainer';
import useDebounce from '../../../hooks/form';
import { paginationProps } from '../../../utils/tableHelper';
import { useEInvoice, useDownloadEInvoicePdf } from '../src/eInvoiceQueries';
import ExpandedEInvoice from './ExpandedEInvoice';
import { TaggedTextInput } from '@components/inputs';

interface EInvoiceRecord {
  investor_id: string;
  investor_name: string;
}

const EInvoiceList = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const { mutateAsync: downloadPdfMutate, isLoading: isDownloadingPdf } = useDownloadEInvoicePdf();

  const { data: eInvoiceData, isLoading: isEInvoiceLoading } = useEInvoice(searchFilter);

  const summaryData: EInvoiceRecord[] = useMemo(() => {
    return (eInvoiceData?.data || []).map((item: any) => ({
      investor_id: item.investor_id,
      investor_name: item.investor_name,
    }));
  }, [eInvoiceData?.data]);

  useDebounce(searchInput, () => {
    setSearchFilter(searchInput);
    setCurrentPage(1);
  });

  const onFilterChange = (changedValues: any) => {
    if ('search' in changedValues) {
      setSearchInput(changedValues.search || '');
    }
  };

  const onDownloadPdf = async (id: number, eInvoiceNumber: string) => {
    try {
      const response = await downloadPdfMutate(id);
      const filename = eInvoiceNumber ? `${eInvoiceNumber}.pdf` : `eInvoice_${id}.pdf`;
      downloadAs(response.data, filename, 'application/pdf');
    } catch {
      notification.error({
        message: 'Download e-Invoice PDF',
        description: 'Something went wrong. Please try again later.',
      });
    }
  };

  const summaryColumns: ColumnsType<EInvoiceRecord> = [
    {
      title: 'Investor ID',
      dataIndex: 'investor_id',
      key: 'investor_id',
      width: 100,
    },
    {
      title: 'Investor Name',
      dataIndex: 'investor_name',
      key: 'investor_name',
    },
  ];

  return (
    <CustomPageContainer breadcrumbName="e-Invoice" title="e-Invoice">
      <ContentContainer>
        <Form layout="vertical" onValuesChange={onFilterChange}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="search" label="Filter by Investor ID/Investor Name">
                <TaggedTextInput tag="search" placeholder="Enter Investor ID/Investor Name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider />

        <Table<EInvoiceRecord>
          loading={isEInvoiceLoading}
          dataSource={summaryData}
          rowClassName={(_, index) => (index % 2 === 0 ? 'dark-stripe' : '')}
          columns={summaryColumns}
          rowKey="investor_name"
          scroll={{
            x: true,
          }}
          pagination={{
            ...paginationProps(eInvoiceData?.total),
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedEInvoice
                investorName={record.investor_name}
                onDownloadPdf={onDownloadPdf}
                isDownloadingPdf={isDownloadingPdf}
              />
            ),
          }}
        />
      </ContentContainer>
    </CustomPageContainer>
  );
};

export default EInvoiceList;
